import type { CSSProperties, ReactNode } from 'react'
import type { NodeRoom, RoomAmount } from '@/api/types'
import { Empty } from '@/components/layout/Page'
import { Progress } from '@/components/ui/progress'
import { tierWord } from '@/domain/storage'
import { cn } from '@/lib/utils'
import { DASH, sizeMb, sizeUsage } from '@/ui/format'

// VM 생성 페이지의 노드 카드(B-37) — 노드마다 작은 카드 하나, 자원마다 가로 막대 한 줄.
// 막대 전체가 노드의 전체이고 채운 부분이 할당, 빈 부분이 남음이다. 할당에는 호스트가 남겨 두는 몫이 들어 있다 — 그래서 할당 + 남음 = 전체다.
// 글자는 `할당 / 전체` 하나다(`12 / 16코어`) — 남은 양을 따로 적지 않는다. 전체를 모르면 막대는 비고 전체가 `—` 다 — 0 이 아니다. 색은 클러스터 화면의 막대(`PoolBar`)와 같은 차트 토큰이고 임계값으로 바꾸지 않는다.
// GPU 막대의 이름에는 한 장의 메모리가 붙는다(`vramMb`) — 모델을 고르는 사람이 그 노드에서 무엇을 받는지 한 줄에서 읽는다.
//
// 두 모양이다. **보기만**(`pick` 없음): 노드를 고를 수 없는 사람의 페이지 맨 아래 `노드 자원` 절이다 — 어디에 놓일지는 배치가 정하고,
// 본 숫자는 본 사람에게 잡혀 있는 것이 아니다. 이 절은 아무것도 잠그지 않고, 어느 노드도 받지 못하는 사양을 막는 것은 페이지의 재기다(B-47).
// **고르기**(`pick`): 노드를 고를 수 있는 사람(F-126)의 맨 위 `노드` 절이다(B-45). 맨 앞이 `자동`(배치가 정한다)이고
// 카드 하나가 곧 라디오다. 지금 고른 사양을 받지 못하는 노드는 빼지 않고 흐리게 서서 사유를 단다(`domain/room.ts` 의 `roomShort`) —
// 목록에서 빼면 사람은 그 노드가 왜 없는지 모른다. 고른 노드가 나중에 맞지 않게 되면 그 자리는 페이지의 막힘이 말한다.

type Bar = { label: string; value: string; fraction: number | null; color: string }

const fraction = (allocated: number, total: number | null) => (total === null || total <= 0 ? null : Math.min(1, Math.max(0, allocated / total)))

const cores = (a: RoomAmount): Bar => ({
  label: 'CPU',
  value: `${a.allocated} / ${a.total ?? DASH}코어`,
  fraction: fraction(a.allocated, a.total),
  color: 'var(--chart-1)',
})

// 할당/전체는 GB 로 맞춘다 — sizeUsage 가 전체의 단위로 둘을 함께 적는다.
const memory = (a: RoomAmount): Bar => ({
  label: '메모리',
  value: sizeUsage(a.allocated / 1024, a.total === null ? null : a.total / 1024),
  fraction: fraction(a.allocated, a.total),
  color: 'var(--chart-2)',
})

function bars(r: NodeRoom, vramMb: (model: string) => number): Bar[] {
  return [
    cores(r.cores),
    memory(r.memory_mb),
    ...r.disks.map((d) => ({
      label: tierWord(d.tier),
      value: sizeUsage(d.allocated_gb, d.total_gb),
      fraction: fraction(d.allocated_gb, d.total_gb),
      color: 'var(--chart-3)',
    })),
    ...r.gpus.map((g) => {
      const vram = vramMb(g.model)
      return {
        label: vram > 0 ? `${g.model} · ${sizeMb(vram)}` : g.model,
        value: `${g.total - g.free} / ${g.total}장`,
        fraction: fraction(g.total - g.free, g.total),
        color: 'var(--chart-4)',
      }
    }),
  ]
}

// 자원마다 한 줄 — 이름 · 막대 · 할당/전체. 이름과 값의 폭이 고정이라 줄이 쌓여도 막대의 시작과 끝이 맞는다.
function RoomBar({ node, bar }: { node: string; bar: Bar }) {
  const name = `${bar.label} 할당 ${bar.value}`
  return (
    <div title={name} className="flex min-w-0 items-center gap-2" style={{ '--bar': bar.color } as CSSProperties}>
      <span className="w-32 flex-none truncate text-[12px] text-muted-foreground">{bar.label}</span>
      <Progress
        aria-label={`${node} ${bar.label} 할당`}
        aria-valuetext={bar.value}
        value={bar.fraction === null ? null : Math.round(bar.fraction * 100)}
        className="h-1.5 min-w-0 flex-1 bg-border [&>div]:bg-(--bar)"
      />
      <span className="w-[84px] flex-none whitespace-nowrap text-right text-[12px] font-medium tabular-nums">{bar.value}</span>
    </div>
  )
}

const noVram = () => 0

/** 노드를 고르는 자리. `value` 는 고른 노드의 id 이고 ''가 `자동` 이다. `locked` 는 그 노드를 고를 수 없는 사유다. */
export type RoomPick = { value: string; onChange: (id: string) => void; locked: (r: NodeRoom) => string | null }

export function NodeRooms({
  id,
  rooms,
  vramMb = noVram,
  pick,
}: {
  /** 고르기 모양의 radiogroup id — 페이지의 막힘이 그리로 간다. */
  id?: string
  rooms: NodeRoom[]
  vramMb?: (model: string) => number
  pick?: RoomPick
}) {
  if (!pick) {
    if (rooms.length === 0) return <Empty>노드 없음</Empty>
    return (
      <ul aria-label="노드 자원" className="grid gap-2 md:grid-cols-2">
        {rooms.map((r) => (
          <li key={r.name} aria-label={r.name} className="flex min-w-0 flex-col gap-1.5 rounded-md border bg-card px-3 py-2.5">
            <div className="truncate text-[13px] font-semibold">{r.name}</div>
            {bars(r, vramMb).map((b) => (
              <RoomBar key={b.label} node={r.name} bar={b} />
            ))}
          </li>
        ))}
      </ul>
    )
  }
  // 고르기의 노드는 id 가 있는 것뿐이다 — 고를 수 있는 사람에게만 서버가 id 를 싣는다.
  const choosable = rooms.filter((r): r is NodeRoom & { id: string } => typeof r.id === 'string')
  return (
    <div id={id} role="radiogroup" aria-label="노드" className="grid gap-2 md:grid-cols-2">
      <PickCard label="자동" checked={pick.value === ''} onPick={() => pick.onChange('')}>
        <span className="text-[13px] font-semibold">자동</span>
      </PickCard>
      {choosable.map((r) => {
        const reason = pick.locked(r)
        return (
          <PickCard key={r.id} label={r.name} checked={pick.value === r.id} disabled={reason !== null && pick.value !== r.id} onPick={() => pick.onChange(r.id)}>
            <span className="flex min-w-0 items-baseline justify-between gap-2">
              <span className="truncate text-[13px] font-semibold">{r.name}</span>
              {reason ? <span className="flex-none text-[11px] text-muted-foreground">{reason}</span> : null}
            </span>
            {bars(r, vramMb).map((b) => (
              <RoomBar key={b.label} node={r.name} bar={b} />
            ))}
          </PickCard>
        )
      })}
    </div>
  )
}

function PickCard({
  label,
  checked,
  disabled = false,
  onPick,
  children,
}: {
  label: string
  checked: boolean
  disabled?: boolean
  onPick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onPick}
      className={cn(
        'flex min-w-0 flex-col gap-1.5 rounded-md border bg-card px-3 py-2.5 text-left',
        // 고른 모양은 OS 타일과 같다.
        checked ? 'border-brand bg-brand/5 ring-1 ring-brand' : 'border-border hover:bg-surface-2',
        disabled && 'opacity-50',
      )}
    >
      {children}
    </button>
  )
}
